import VuexPersistence from 'vuex-persist';
import { createStore } from 'vuex';

// import modules
import { cloneDeep } from 'lodash';
import { getAccessLevel } from '@/utils/MemberUtils';
import MemberDetails from './modules/MemberDetails';
import GenerateMemberCards from './modules/GenerateMemberCards';
import RebateNomination from './modules/RebateNomination';
import BenefitQuotesClaims from './modules/BenefitQuotesClaims';
import MakeAPayment from './modules/MakeAPayment';
import TaxStatements from './modules/TaxStatements';
import PaymentDetails from './modules/PaymentDetails';
import WaitingPeriods from './modules/WaitingPeriods';
import LimitsUsage from './modules/LimitsUsage';
import ChangeCoverRoot from './modules/ChangeCoverRoot';
import AddRemovePerson from './modules/AddRemovePerson';
import ChangeExcess from './modules/ChangeExcess';
import Rebates from './modules/Rebates';

const DEFAULT_THIS_POLICY = 'not-selected';

const vuexSessionStorage = new VuexPersistence({
  storage: window.sessionStorage,
});

const initialState = {
  currentMember: {},
  logout: false,
  thisPolicy: window.localStorage.getItem('currentPolicyNumber') || DEFAULT_THIS_POLICY,
  thisMembership: null,
  membershipInfo: null,
  members: null,
};

export default createStore({
  state: cloneDeep(initialState),

  mutations: {
    updateCurrentMemberDetails(state, data) {
      state.currentMember = data;
    },

    updateLogout(state) {
      state.logout = true;
    },

    // if `data` was not given, default to DEFAULT_THIS_POLICY value
    saveThisPolicyNumber(state, data = DEFAULT_THIS_POLICY) {
      state.thisPolicy = data;
      window.localStorage.setItem('currentPolicyNumber', data);

      // Also update session storage so VuexPersistence stays in sync
      // (needed when mutation is called directly outside of store.commit)
      try {
        const vuexState = JSON.parse(window.sessionStorage.getItem('vuex')) || {};
        vuexState.thisPolicy = data;
        window.sessionStorage.setItem('vuex', JSON.stringify(vuexState));
      } catch (e) { /* ignore */ }
    },

    saveThisMembership(state, data) {
      state.thisMembership = data;
    },

    updateMembershipInfo(state, data) {
      // For the initial load where `state.membershipInfo` is null, add all `data` to `state.membershipInfo`
      if (!state.membershipInfo) {
        state.membershipInfo = data;
      }

      // Second load onwards, when the `state.membershipInfo` might already have data, we don't want to overwrite data when value is null, hence cleaning it below
      // cloneDeep so that original object is not modified because that's being used in the component after this update
      const nonNullData = cloneDeep(data);

      // Remove null/undefined/'' values from data
      Object.keys(nonNullData).forEach((key) => {
        if (!nonNullData[key]) delete nonNullData[key];
      });

      // Object destructuring so that old data is updated with new non-null values data, keeping intact the old values where it was null/undefined/''
      state.membershipInfo = { ...state.membershipInfo, ...nonNullData };
    },

    resetPolicy(state) {
      state.thisPolicy = 'not-selected';
      window.localStorage.removeItem('currentPolicyNumber');
    },

    updateMembers(state, data) {
      state.members = data;
    },

    resetFullStore(state) {
      Object.assign(state, cloneDeep(initialState));
    },
  },

  getters: {
    getCurrentPolicyIsExtrasFlag: (state) => {
      if (state.thisMembership !== null && state.thisPolicy !== 'not-selected') {
        return state.thisMembership.productList.find((obj) => {
          return obj.memberNumber === state.thisPolicy;
        }).isExtras;
      }

      return null;
    },

    getCurrentPolicyExtrasOnlyFlag: (state) => {
      if (state.thisMembership !== null && state.thisPolicy !== 'not-selected') {
        return state.thisMembership.productList.find((obj) => {
          return obj.memberNumber === state.thisPolicy;
        }).extrasOnly;
      }

      return null;
    },

    getCurrentMembership: (state) => {
      return state.thisMembership;
    },

    getCurrentPolicyNumber: (state) => {
      return state.thisPolicy;
    },

    getMembershipInfo: (state) => {
      return state.membershipInfo;
    },

    getCurrentPersonCode: (state) => {
      return state.currentMember.personCode;
    },

    getLogoutState: (state) => {
      return state.logout;
    },

    memberships: (state) => {
      return state.currentMember.memberships;
    },

    getCurrentMemberDetails: (state) => {
      return state.currentMember;
    },

    getMembers: (state) => {
      return state.members;
    },

    getAccessLevel(state) {
      return getAccessLevel(state.thisPolicy, state.currentMember?.memberships);
    },
  },

  modules: {
    MemberDetails,
    GenerateMemberCards,
    RebateNomination,
    BenefitQuotesClaims,
    MakeAPayment,
    TaxStatements,
    PaymentDetails,
    WaitingPeriods,
    LimitsUsage,
    ChangeCoverRoot,
    AddRemovePerson,
    ChangeExcess,
    Rebates,
  },

  plugins: [vuexSessionStorage.plugin],
});
